import { SUPPORT_ATTACHMENT_MAX_BYTES } from '@asyagrup/group-hub-contracts';

/**
 * A picture of the screen the reporter is looking at.
 *
 * WHY A LIBRARY. There is no browser API that rasterises the current document.
 * `getDisplayMedia` exists, but it opens a share picker and asks the person to
 * choose their own tab — two extra decisions in the moment they are already
 * annoyed enough to be reporting a bug. `modern-screenshot` walks the DOM and
 * paints it, and it is loaded HERE, dynamically, so nobody who never presses
 * the button pays for it.
 *
 * EVERY OPTION BELOW WAS MEASURED AGAINST THE LIVE APP, because the obvious
 * configuration produced an empty file and shipped looking fine:
 *
 *   document.body, JPEG            0 KB      — empty blob
 *   document.body, PNG, no fonts   0 KB      — empty blob, 6.1 s
 *   main, PNG, default             26 KB     — 8.5 s
 *   main, PNG, no fonts            24 KB     — 15 s cold, 2.7 s warm
 *   main, PNG, no fonts, styles    17 KB     — 1.6 s cold          ← this
 *
 * - **`main`, not `body`.** Rasterising the whole body returns an empty blob in
 *   this layout, and it is the sticky header that does it. Nothing is lost: the
 *   header shows no state, and which screen this was is recorded as the
 *   ticket's `pageUrl` rather than left to be read off a picture.
 * - **PNG, not JPEG.** JPEG has no alpha and the canvas conversion returns an
 *   empty blob without a background; PNG at this size is 24 KB, so the reason
 *   to prefer JPEG never applied.
 * - **`font: false`.** Embedding web fonts costs three times the wall clock —
 *   8.5 s against 2.7 s — for text that renders legibly in the fallback face. A
 *   bug report nobody waits for is a bug report nobody files.
 * - **`backgroundColor` from the live body.** Without it a dark-theme screen
 *   comes out as light text on transparency, which flattens to unreadable.
 *
 * WHY IT MAY STILL RETURN NULL. Rasterising is best-effort. A failed screenshot
 * must never cost the report — the whole point is that reporting is one click.
 * Null means "no picture", not "no ticket".
 */
const TIMEOUT_MS = 10_000;

/**
 * The properties copied onto every cloned node.
 *
 * This list is the difference between a screenshot and no screenshot. Left to
 * copy every computed property, the FIRST capture on a page takes 15 seconds —
 * every later one takes 3, because the work is building the style cache once.
 * Nobody gets a second capture: they press the button once, on a screen that is
 * already misbehaving. Restricting the set brings the cold path to 1.6 s,
 * measured on the live app, on the same screen, back to back.
 *
 * Layout, box, type and colour are here because a screenshot missing them is
 * not a picture of the screen. Everything decorative that costs more than it
 * shows — transitions, filters, animations — is not.
 */
const STYLE_PROPERTIES = [
  'color',
  'background-color',
  'background-image',
  'border',
  'border-radius',
  'box-shadow',
  'outline',
  'display',
  'flex',
  'flex-direction',
  'flex-wrap',
  'align-items',
  'justify-content',
  'gap',
  'grid-template-columns',
  'grid-column',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'line-height',
  'letter-spacing',
  'text-align',
  'text-decoration',
  'text-transform',
  'white-space',
  'height',
  'min-height',
  'max-height',
  'width',
  'min-width',
  'max-width',
  'margin',
  'padding',
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'overflow',
  'opacity',
  'visibility',
  'transform',
  'z-index',
] as const;

export async function captureScreen(): Promise<File | null> {
  try {
    const target = document.querySelector('main');
    if (!target) return null;

    const { domToBlob } = await import('modern-screenshot');

    const blob = await Promise.race([
      domToBlob(target, {
        font: false,
        backgroundColor: getComputedStyle(document.body).backgroundColor || '#ffffff',
        scale: 1,
        includeStyleProperties: [...STYLE_PROPERTIES],
        // Scaling down was measured too: 0.75 produced a smaller file and took
        // LONGER (4.5 s), so there is nothing to buy here.
        filter: (node: Node) =>
          !(node instanceof Element && node.hasAttribute('data-no-screenshot')),
      }),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), TIMEOUT_MS)),
    ]);

    // An empty blob is the failure mode this file exists to guard against: it
    // uploads without complaint and opens as nothing.
    if (!blob || blob.size === 0) return null;
    if (blob.size > SUPPORT_ATTACHMENT_MAX_BYTES) return null;

    return new File([blob], `ekran-${stamp()}.png`, { type: 'image/png' });
  } catch {
    // Deliberately silent to the user: they asked to report a bug, not to
    // debug the reporter. The absence of the attachment is the whole signal.
    return null;
  }
}

function stamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}
